import type { Action } from 'redux';
import type { Action as SagaAction, Channel } from 'redux-saga';
import type { ActionPattern } from 'redux-saga/effects';
import { all, put, take, actionChannel, select, call, takeEvery } from 'redux-saga/effects';

import type { CrumbRoute } from 'constants/EntityTitles';
import { closeConcurrencyModal, openConcurrencyModal } from 'modules/app/actions';
import { CLOSE_CONCURRENCY_MODAL, CONCURRENCY_REFRESH_PAGE } from 'modules/app/actionTypes';
import { scopeToPage } from 'modules/app/sagas/utils';
import { getCurrentUserId } from 'modules/users/selectors/currentUser';

import * as actions from './actions';
import * as actionTypes from './actionTypes';
import { topics } from './socket.middleware';
import type { PutSocketMessageAction } from './types/actions';

const triggerConcurrencySocketUpdate = (refreshFn?: () => void) =>
  // eslint-disable-next-line consistent-return
  function* socketUpdateWorker() {
    yield put(openConcurrencyModal());
    const { type }: Action = yield take([CLOSE_CONCURRENCY_MODAL, CONCURRENCY_REFRESH_PAGE]);
    if (type === CONCURRENCY_REFRESH_PAGE) {
      if (typeof refreshFn === 'function') {
        const result: unknown = yield all([call(refreshFn), put(closeConcurrencyModal())]);
        return result;
      }
      window.location.reload();
    }
  };

export const concurrencyWatcher = (
  type: string,
  refreshFn?: () => void,
  matcher?: (action: PutSocketMessageAction) => boolean,
) => {
  /* Avoid to trigger the concurrency modal for after own update */
  const patternMatcher = (action: PutSocketMessageAction) => {
    const { type: actionType, payload } = action;
    const basicMatching = actionType === type && typeof payload?.createdById !== 'undefined';
    return basicMatching && (matcher?.(action) ?? true);
  };

  // eslint-disable-next-line func-names
  return function* () {
    yield select(getCurrentUserId);
    yield takeEvery(patternMatcher as (action: SagaAction) => boolean, triggerConcurrencySocketUpdate(refreshFn));
  };
};

export function* WSSubscribe(topic: string, getParams: (state: unknown) => object, modifiers?: ActionPattern) {
  const payload: object = yield select(getParams);
  yield put(actions.subscribe(topic, payload));
  yield take([actionTypes.SUBSCRIBED, actionTypes.ERROR]);

  if (modifiers) {
    const channel: Channel<SagaAction> = yield actionChannel(modifiers);
    do {
      yield take(channel);
      const updaterPayload: object = yield select(getParams);
      yield put(actions.subscribe(topic, updaterPayload));
    } while (true);
  }
}

export function* WBUnsubscribe(topic: string) {
  if (topics.has(topic)) {
    yield put(actions.unsubscribe(topic));
    yield take([actionTypes.UNSUBSCRIBED, actionTypes.ERROR]);
  }
}

export function* subscribeOnPage(
  paths: CrumbRoute[],
  topic: string,
  getParams: (state: unknown) => object,
  modifiers?: ActionPattern,
  channelPattern?: ActionPattern,
) {
  yield scopeToPage(
    paths,
    function* onPage() {
      yield call(WSSubscribe, topic, getParams, modifiers);
    },
    function* onLeave() {
      yield call(WBUnsubscribe, topic);
    },
    channelPattern,
  );
}
